import React, { useState, useEffect } from "react";
import { ListItem as ListItemEntity, List as ListEntity } from "@/entities/all";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { MoreVertical, Edit2, Trash2, GripVertical } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useData } from "../DataContext";
import type { ListItem } from "@/types";

interface ListItemComponentProps {
  item: ListItem;
  inherited?: boolean;
  listId?: string;
}

export default function ListItemComponent({ item, inherited = false, listId }: ListItemComponentProps) {
  const { folders, lists, sections, listItems, inheritedCompletions, runOptimisticUpdate, setOptimisticData } = useData();
  const [isEditing, setIsEditing] = useState(false);
  const [content, setContent] = useState(item.content);

  useEffect(() => {
    if (!isEditing) {
      setContent(item.content);
    }
  }, [item.content, isEditing]);

  const handleSave = () => {
    if (!content.trim() || content === item.content) {
      setIsEditing(false);
      setContent(item.content);
      return;
    }

    const originalState = { listItems: [...listItems] };
    const task = async () => ListItemEntity.update(item.id, { content: content.trim() });
    const newState = {
      folders: [...folders],
      lists: [...lists],
      sections: [...sections],
      listItems: listItems.map(i => 
        i.id === item.id ? { ...i, content: content.trim() } : i
      )
    };

    runOptimisticUpdate(task, { update: newState, originalState: { folders, lists, sections, ...originalState } });
    setIsEditing(false);
  };

  const getInheritedItemState = (): boolean => {
    if (!inherited || !listId) return item.completed;
    const serverCompletion = inheritedCompletions?.find(c => c.list_id === listId && c.item_id === item.id);
    if (serverCompletion !== undefined) {
      return serverCompletion.completed;
    }
    const inheritanceKey = `${item.id}_${listId}`;
    try {
      const states = JSON.parse(localStorage.getItem('inheritedItemStates') || '{}');
      return Boolean(states[inheritanceKey]);
    } catch {
      return false;
    }
  };

  const isCompleted = getInheritedItemState();

  const handleToggleComplete = async () => {
    if (inherited && listId) {
      const newStatus = !isCompleted;
      
      // Update localStorage for immediate local sync
      const inheritanceKey = `${item.id}_${listId}`;
      try {
        const currentState = JSON.parse(localStorage.getItem('inheritedItemStates') || '{}');
        currentState[inheritanceKey] = newStatus;
        localStorage.setItem('inheritedItemStates', JSON.stringify(currentState));
      } catch (e) {
        console.error('LocalStorage error:', e);
      }

      const task = async () => {
        try {
          await ListEntity.setInheritedItemCompleted(listId, item.id, newStatus);
        } catch (e) {
          console.error('Failed to persist inherited item status to server:', e);
        }
      };

      const updatedCompletions = [
        ...(inheritedCompletions || []).filter(c => !(c.list_id === listId && c.item_id === item.id)),
        { list_id: listId, item_id: item.id, completed: newStatus }
      ];

      setOptimisticData(prev => ({
        ...prev,
        inheritedCompletions: updatedCompletions
      }));

      runOptimisticUpdate(task, {
        update: prev => ({ ...prev, inheritedCompletions: updatedCompletions })
      }).catch(() => {});
    } else {
      const originalState = { listItems: [...listItems] };
      const newCompleted = !item.completed;
      const task = async () => ListItemEntity.update(item.id, { completed: newCompleted });
      const newState = {
        folders: [...folders],
        lists: [...lists],
        sections: [...sections],
        listItems: listItems.map(i => 
          i.id === item.id ? { ...i, completed: newCompleted } : i
        )
      };
      runOptimisticUpdate(task, { update: newState, originalState: { folders, lists, sections, ...originalState } });
    }
  };

  const handleDelete = () => {
    if (inherited) return;
    
    const originalState = { listItems: [...listItems] };
    const task = async () => ListItemEntity.delete(item.id);
    const newState = {
      folders: [...folders],
      lists: [...lists],
      sections: [...sections],
      listItems: listItems.filter(i => i.id !== item.id)
    };
    runOptimisticUpdate(task, { update: newState, originalState: { folders, lists, sections, ...originalState } });
  };

  if (inherited) {
    return (
      <div className="flex items-start gap-3 p-3 sm:p-3 min-h-[48px] touch-manipulation">
        <Checkbox 
          checked={isCompleted} 
          onCheckedChange={handleToggleComplete}
          className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 mt-1 flex-shrink-0 w-5 h-5"
        />
        <span className={`flex-1 text-slate-700 break-words text-sm sm:text-base leading-relaxed ${isCompleted ? 'line-through opacity-70' : ''}`}>
          {item.content}
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-2 sm:gap-3 p-3 sm:p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors group min-h-[48px] touch-manipulation">
      <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
        <Checkbox 
          checked={item.completed} 
          onCheckedChange={handleToggleComplete}
          className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 mt-1 flex-shrink-0 w-5 h-5"
        />
        
        {isEditing ? (
          <Input
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className="flex-1 border-slate-200 bg-white focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-0 focus-visible:border-blue-500 text-sm sm:text-base"
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave();
              if (e.key === 'Escape') {
                setIsEditing(false);
                setContent(item.content);
              }
            }}
            onBlur={handleSave}
            autoFocus
          />
        ) : (
          <span 
            className={`flex-1 cursor-pointer break-words text-sm sm:text-base leading-relaxed min-w-0 ${item.completed ? 'line-through opacity-60' : ''}`}
            onClick={() => setIsEditing(true)}
          >
            {content}
          </span>
        )}
      </div>

      <div className="flex items-center flex-shrink-0">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              size="icon" 
              className="p-1 sm:p-2 w-6 h-6 sm:w-8 sm:h-8 opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
            >
              <MoreVertical className="w-3 h-3 sm:w-4 sm:h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setIsEditing(true)}>
              <Edit2 className="w-4 h-4 mr-2" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={handleDelete}
              className="text-red-600 focus:text-red-600"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <GripVertical className="w-4 h-4 sm:w-5 sm:h-5 text-slate-300 cursor-grab group-hover:text-slate-400 transition-colors hidden sm:block" />
      </div>
    </div>
  );
}
